import { useState, useEffect, useCallback } from "react";
import { useMailReading } from "@/lib/mail-reading-context";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import Logo from "@/components/logo";
import {
  useGetMe,
  useGetMailUnreadSummary,
  getGetMailUnreadSummaryQueryKey,
  useGetMailOldThreadCandidatesSummary,
  getGetMailOldThreadCandidatesSummaryQueryKey,
  useGetMailPendingActionsSummary,
  getGetMailPendingActionsSummaryQueryKey,
  useGetAssistantAnalyzeJob,
  getGetAssistantAnalyzeJobQueryKey,
  useListMailAccounts,
  getListMailAccountsQueryKey,
  useListDelegatedTasks,
  getListDelegatedTasksQueryKey,
  useGetToday,
  getGetTodayQueryKey,
} from "@workspace/api-client-react";
import {
  Sun,
  Mail as MailIcon,
  Inbox as InboxIcon,
  Folder,
  Settings as SettingsIcon,
  CheckSquare,
  MessageSquare,
  Shield,
  Loader2,
  Layers,
  BookOpen,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  Users,
  LayoutTemplate,
  ChevronDown,
  ScrollText,
} from "lucide-react";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { useImpersonation } from "@/hooks/use-impersonation";
import { stopImpersonation } from "@/lib/impersonation";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { listProjectSuggestions } from "@/lib/project-suggestions-api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CaptureFab from "./capture-fab";
import FirstSyncRecapDialog from "./first-sync-recap-dialog";
import CommandPalette from "./command-palette";
import KeyboardShortcutsDialog from "./keyboard-shortcuts-dialog";
import CaptureDialog from "./capture-dialog";
import AiBudgetBanner from "./ai-budget-banner";
import LanguageSwitcher from "./language-switcher";
import DemoModeBanner from "./demo-mode-banner";
import MailConnectBanner from "./mail-connect-banner";
import DemoTutorial from "./demo-tutorial";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useClerk } from "@clerk/react";

const ANALYSIS_JOB_STORAGE_KEY = "onenext.onboarding.analysisJobId";

function readStoredJobId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ANALYSIS_JOB_STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as { jobId?: string }).jobId ?? null;
  } catch {
    return null;
  }
}

function BackgroundSyncIndicator() {
  const [jobId, setJobId] = useState<string | null>(readStoredJobId);

  const job = useGetAssistantAnalyzeJob(jobId ?? "", {
    query: {
      queryKey: getGetAssistantAnalyzeJobQueryKey(jobId ?? ""),
      enabled: !!jobId,
      refetchInterval: (query) => {
        const s = (query.state.data as { status?: string } | undefined)?.status;
        return s === "running" || s === "paused" ? 3000 : false;
      },
    },
  });

  useEffect(() => {
    if (!job.data) return;
    const s = job.data.status;
    if (s === "done" || s === "failed" || s === "cancelled") {
      window.localStorage.removeItem(ANALYSIS_JOB_STORAGE_KEY);
      setJobId(null);
    }
  }, [job.data]);

  if (!jobId || !job.data || job.data.status === "done") return null;

  const done = (job.data.inboxDone ?? 0) + (job.data.mailsDone ?? 0);
  const total = (job.data.inboxTotal ?? 0) + (job.data.mailsTotal ?? 0);
  const isSyncing = (job.data.phase ?? "analyzing") === "syncing";

  return (
    <div className="mx-3 mb-2 px-2.5 py-1.5 rounded-md bg-primary/8 flex items-center gap-2 min-w-0">
      <Loader2 className="w-3 h-3 animate-spin text-primary shrink-0" />
      <span className="text-xs text-sidebar-foreground/60 truncate">
        {isSyncing
          ? "Synchronisation en cours…"
          : total > 0
            ? `Analyse ${done} / ${total}`
            : "Analyse en cours…"}
      </span>
    </div>
  );
}

function getInitials(name?: string | null, email?: string) {
  if (name) return name.substring(0, 2).toUpperCase();
  if (email) return email.substring(0, 2).toUpperCase();
  return "U";
}

interface NavItem {
  href: string;
  label: string;
  icon: typeof Sun;
  adminOnly?: boolean;
}

function NavLinks({
  location,
  onNavigate,
  variant = "sidebar",
  mailUnread = 0,
  pendingActions = 0,
  delegatedOverdue = 0,
  urgentTasks = 0,
  projectSuggestionsPending = 0,
  showAdmin = false,
  collapsed = false,
  mailSyncStatus = null,
}: {
  location: string;
  onNavigate?: () => void;
  variant?: "sidebar" | "sheet";
  mailUnread?: number;
  pendingActions?: number;
  delegatedOverdue?: number;
  urgentTasks?: number;
  projectSuggestionsPending?: number;
  showAdmin?: boolean;
  collapsed?: boolean;
  mailSyncStatus?: "ok" | "syncing" | "error" | null;
}) {
  const { t } = useTranslation();
  const [adminOpen, setAdminOpen] = useState(false);

  const NAV_ITEMS: NavItem[] = [
    { href: "/app/today",    label: t("app_nav.maintenant"), icon: Sun },
    { href: "/app/actions",  label: t("app_nav.en_attente"), icon: CheckSquare },
    { href: "/app/inbox",    label: t("app_nav.inbox"),      icon: InboxIcon },
    { href: "/app/mail",     label: t("app_nav.mail"),       icon: MailIcon },
    { href: "/app/projects", label: t("app_nav.projects"),   icon: Folder },
    { href: "/app/crm",      label: t("app_nav.contacts"),   icon: Users },
    { href: "/app/library",  label: t("app_nav.documents"),  icon: BookOpen },
    { href: "/app/chat",     label: t("app_nav.assistant"),  icon: MessageSquare },
  ];

  const SECTION_BREAKS_AFTER = new Set(["/app/actions", "/app/library"]);

  const ADMIN_ITEMS: NavItem[] = [
    { href: "/admin/posts", label: "Posts",            icon: LayoutTemplate, adminOnly: true },
    { href: "/admin",       label: t("app_nav.admin"), icon: Shield,         adminOnly: true },
  ];

  const base =
    variant === "sheet"
      ? "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer"
      : "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer";

  const getBadgeValue = (item: NavItem) =>
    item.href === "/app/today"
      ? urgentTasks
      : item.href === "/app/mail"
        ? mailUnread
        : item.href === "/app/actions"
          ? delegatedOverdue
          : item.href === "/app/projects"
            ? projectSuggestionsPending
            : 0;

  const renderNavItem = (item: NavItem) => {
    const isActive = location === item.href;
    const badgeValue = getBadgeValue(item);
    const badgeTestId =
      item.href === "/app/actions" ? `nav-badge-actions-${variant}` : undefined;

    const row = (
      <Link key={item.href} href={item.href} onClick={onNavigate}>
        <span
          className={`${base} ${
            isActive
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
          }`}
        >
          <item.icon className="w-4 h-4 shrink-0" />
          <span className="flex-1">{item.label}</span>
          {badgeValue > 0 && (
            <span
              className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-primary/15 text-primary text-[10px] font-semibold flex items-center justify-center"
              data-testid={badgeTestId}
            >
              {badgeValue > 99 ? "99+" : badgeValue}
            </span>
          )}
          {item.href === "/app/mail" && mailSyncStatus !== null && (
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${badgeValue > 0 ? "" : "ml-auto"} ${
                mailSyncStatus === "error"
                  ? "bg-destructive"
                  : mailSyncStatus === "syncing"
                    ? "bg-amber-500"
                    : "bg-green-500"
              }`}
              data-testid="nav-mail-sync-status-dot-expanded"
            />
          )}
        </span>
      </Link>
    );

    if (SECTION_BREAKS_AFTER.has(item.href)) {
      return (
        <div key={item.href}>
          {row}
          <div className="my-2 mx-1 h-px bg-sidebar-border/60" />
        </div>
      );
    }
    return row;
  };

  if (collapsed) {
    const renderCollapsedItem = (item: NavItem) => {
      const isActive = location === item.href;
      const badgeValue = getBadgeValue(item);
      const badgeTestId =
        item.href === "/app/actions" ? `nav-badge-actions-${variant}` : undefined;

      const icon = (
        <Tooltip key={item.href} delayDuration={300}>
          <TooltipTrigger asChild>
            <Link href={item.href} onClick={onNavigate}>
              <span
                className={`relative flex items-center justify-center w-9 h-9 rounded-lg mx-auto transition-colors cursor-pointer ${
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                }`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.adminOnly && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-violet-600 flex items-center justify-center ring-2 ring-sidebar">
                    <Shield className="w-2 h-2 text-white" />
                  </span>
                )}
                {badgeValue > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center"
                    data-testid={badgeTestId}
                  >
                    {badgeValue > 99 ? "99+" : badgeValue}
                  </span>
                )}
                {item.href === "/app/mail" && mailSyncStatus !== null && (
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-sidebar ${
                      mailSyncStatus === "error"
                        ? "bg-destructive"
                        : mailSyncStatus === "syncing"
                          ? "bg-amber-500"
                          : "bg-green-500"
                    }`}
                    data-testid="nav-mail-sync-status-dot"
                  />
                )}
              </span>
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <span className="flex items-center gap-1.5">
              {item.label}
              {item.adminOnly && (
                <span className="text-[9px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 px-1 py-0.5 rounded">
                  Admin
                </span>
              )}
            </span>
            {item.href === "/app/mail" && mailSyncStatus === "error" && (
              <span className="block text-destructive text-[10px] font-normal">Erreur de synchronisation</span>
            )}
          </TooltipContent>
        </Tooltip>
      );

      if (SECTION_BREAKS_AFTER.has(item.href)) {
        return (
          <div key={item.href}>
            {icon}
            <div className="my-2 h-px bg-sidebar-border/60 mx-1" />
          </div>
        );
      }
      return icon;
    };

    return (
      <nav className="space-y-1" aria-label={t("app_nav.nav_aria")}>
        {NAV_ITEMS.map(renderCollapsedItem)}
        {showAdmin && (
          <>
            <div className="py-1">
              <div className="h-px bg-sidebar-border mx-1" />
            </div>
            {ADMIN_ITEMS.map(renderCollapsedItem)}
          </>
        )}
      </nav>
    );
  }

  return (
    <nav className="space-y-1" aria-label={t("app_nav.nav_aria")}>
      {NAV_ITEMS.map(renderNavItem)}
      {showAdmin && (
        <div className="pt-2 mt-1">
          <div className="h-px bg-sidebar-border mb-2" />
          <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/50 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground transition-colors">
                <Shield className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left text-xs uppercase tracking-wider font-semibold">Administration</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${adminOpen ? "rotate-180" : ""}`}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1 space-y-0.5">
              {ADMIN_ITEMS.map((item) => {
                const isActive = location === item.href;
                return (
                  <Link key={item.href} href={item.href} onClick={onNavigate}>
                    <span
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        isActive
                          ? "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300"
                          : "text-sidebar-foreground/60 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-sidebar-foreground"
                      }`}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      <span className="flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 px-1.5 py-0.5 rounded">
                        <Shield className="w-2.5 h-2.5" />
                        Admin
                      </span>
                    </span>
                  </Link>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </nav>
  );
}

function DesktopUserBlock({
  name,
  email,
  oldThreadPending,
}: {
  name?: string | null;
  email?: string;
  oldThreadPending: number;
}) {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  return (
    <div className="space-y-1">
      <Link href="/app/settings">
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-sidebar-accent/50 transition-colors"
          data-testid="link-settings-desktop"
        >
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {getInitials(name, email)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {name || t("app_nav.user_fallback")}
            </p>
            <p className="text-xs text-sidebar-foreground/50 truncate">{email}</p>
          </div>
          <div className="relative shrink-0">
            <SettingsIcon className="w-4 h-4 text-sidebar-foreground/50" />
            {oldThreadPending > 0 && (
              <span
                aria-label={t("shell.old_threads_aria", { count: oldThreadPending })}
                className="absolute -top-1.5 -right-1.5 min-w-[1rem] h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center"
                data-testid="badge-old-threads-desktop"
              >
                {oldThreadPending > 99 ? "99+" : oldThreadPending}
              </span>
            )}
          </div>
        </div>
      </Link>
      <button
        onClick={() => signOut({ redirectUrl: "/" })}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
        data-testid="btn-signout-desktop"
      >
        <LogOut className="w-4 h-4 shrink-0" />
        <span>Déconnexion</span>
      </button>
    </div>
  );
}

const SIDEBAR_STORAGE_KEY = "onenext.sidebar.collapsed";

export default function AppShell({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { data: user } = useGetMe();
  const [profileOpen, setProfileOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { signOut } = useClerk();
  const { active: isDemoActive, disable: disableDemo, enable: enableDemo } = useDemoMode();

  const { isMailReading } = useMailReading();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);

  // ── Global keyboard shortcuts ──────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      // Skip when focus is in an input/textarea/select/contenteditable
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable
      )
        return;

      // Cmd+K / Ctrl+K — command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }

      // Single-key shortcuts (no modifier)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "?":
          e.preventDefault();
          setShortcutsOpen((o) => !o);
          break;
        case "n":
        case "N":
          e.preventDefault();
          setCaptureOpen((o) => !o);
          break;
        case "t":
        case "T":
          e.preventDefault();
          setLocation("/app/today");
          break;
        case "i":
        case "I":
          e.preventDefault();
          setLocation("/app/inbox");
          break;
        case "p":
        case "P":
          e.preventDefault();
          setLocation("/app/projects");
          break;
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setLocation]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const effectivelyCollapsed = sidebarCollapsed || isMailReading;

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const { data: unreadData } = useGetMailUnreadSummary({
    query: { queryKey: getGetMailUnreadSummaryQueryKey(), refetchInterval: 60_000 },
  });
  const mailUnread = unreadData?.total ?? 0;

  const { data: oldThreadData } = useGetMailOldThreadCandidatesSummary({
    query: {
      queryKey: getGetMailOldThreadCandidatesSummaryQueryKey(),
      refetchInterval: 5 * 60_000,
    },
  });
  const oldThreadPending = oldThreadData?.totalPending ?? 0;

  const { data: pendingActionsData } = useGetMailPendingActionsSummary({
    query: {
      queryKey: getGetMailPendingActionsSummaryQueryKey(),
      refetchInterval: 60_000,
    },
  });
  const pendingActions = pendingActionsData?.total ?? 0;

  const { data: delegatedTasksData } = useListDelegatedTasks({
    query: {
      queryKey: getListDelegatedTasksQueryKey(),
      staleTime: 5 * 60_000,
      refetchInterval: 5 * 60_000,
    },
  });
  const delegatedOverdue = (delegatedTasksData ?? []).filter((d) => d.daysSince > 5).length;

  const { data: todayData } = useGetToday({
    query: {
      queryKey: getGetTodayQueryKey(),
      staleTime: 5 * 60_000,
      refetchInterval: 5 * 60_000,
    },
  });
  const urgentTasks = [
    ...(todayData?.dueToday ?? []),
    ...(todayData?.overdue ?? []),
  ].filter((task) => task.priority === "high" && task.status !== "done").length;

  // Suggestions de projets en attente (regroupements de tâches proposés par
  // l'IA) → badge sur "Projets", comme les suggestions de tâches sur l'inbox.
  const { data: projectSuggestionsData } = useQuery({
    queryKey: ["nav-project-suggestions"],
    queryFn: listProjectSuggestions,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  const projectSuggestionsPending =
    projectSuggestionsData?.suggestions?.length ?? 0;

  const { data: mailAccountsData } = useListMailAccounts({
    query: { queryKey: getListMailAccountsQueryKey(), refetchInterval: 60_000 },
  });
  const mailSyncStatus: "ok" | "syncing" | "error" | null = !mailAccountsData || mailAccountsData.length === 0
    ? null
    : mailAccountsData.some((a) => a.syncStatus === "error")
      ? "error"
      : mailAccountsData.some((a) => a.syncStatus === "syncing")
        ? "syncing"
        : "ok";

  const { isAdmin } = useIsAdmin();
  const impersonation = useImpersonation();
  const qc = useQueryClient();

  const initials = getInitials(user?.name, user?.email);

  const handleStopImpersonation = () => {
    stopImpersonation();
    qc.clear();
  };

  const handleEnableDemo = () => {
    enableDemo();
    setLocation("/app/today");
  };

  const handleDisableDemo = () => {
    disableDemo();
  };

  const handleStartTour = () => {
    handleEnableDemo();
  };

  return (
    <div className={`flex bg-background font-sans ${isMailReading ? "h-screen overflow-hidden" : "min-h-screen"}`}>
      {/* ── Desktop sidebar ───────────────────────────────────── */}
      <TooltipProvider delayDuration={300}>
      <aside
        className={`hidden md:flex flex-col border-r border-border bg-sidebar h-screen sticky top-0 transition-all duration-300 overflow-hidden ${
          effectivelyCollapsed ? "w-16" : "w-64"
        }`}
      >
        {/* Top: logo + nav */}
        <div className={`flex-1 flex flex-col min-h-0 ${effectivelyCollapsed ? "px-3 pt-4" : "p-6 pb-3"}`}>
          {/* Logo */}
          <Link href="/">
            {effectivelyCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center mb-6 cursor-pointer select-none">
                    <Logo size={28} />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>OneNext</TooltipContent>
              </Tooltip>
            ) : (
              <div className="flex items-center gap-2 mb-6 cursor-pointer select-none">
                <Logo size={30} />
                <span className="font-bold text-xl tracking-tight text-sidebar-foreground">
                  OneNext
                </span>
              </div>
            )}
          </Link>

          {/* Nav links — scrollable so admin items never overlap the bottom strip */}
          <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
            <NavLinks
              location={location}
              mailUnread={mailUnread}
              pendingActions={pendingActions}
              delegatedOverdue={delegatedOverdue}
              urgentTasks={urgentTasks}
              projectSuggestionsPending={projectSuggestionsPending}
              showAdmin={isAdmin && !impersonation}
              collapsed={effectivelyCollapsed}
              mailSyncStatus={mailSyncStatus}
            />
          </div>
        </div>

        {/* Bottom section: Visite guidée + demo toggle */}
        <div className="pt-3 border-t border-sidebar-border px-3 space-y-1">
          {effectivelyCollapsed ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleStartTour}
                    className="flex items-center justify-center w-10 h-10 rounded-lg mx-auto text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
                  >
                    <BookOpen className="w-4 h-4 shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>Visite guidée</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={isDemoActive ? handleDisableDemo : handleEnableDemo}
                    className={`flex items-center justify-center w-10 h-10 rounded-lg mx-auto transition-colors ${
                      isDemoActive
                        ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/60"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                  >
                    <Layers className="w-4 h-4 shrink-0" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  {isDemoActive ? "Mode démo actif" : "Essayer la démo"}
                </TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <button
                onClick={handleStartTour}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
              >
                <BookOpen className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left">Visite guidée</span>
              </button>
              {isDemoActive ? (
                <button
                  onClick={handleDisableDemo}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-colors"
                >
                  <Layers className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">Mode démo actif</span>
                  <X className="w-3.5 h-3.5 opacity-60" />
                </button>
              ) : (
                <button
                  onClick={handleEnableDemo}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
                >
                  <Layers className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">Essayer la démo</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Language switcher — hidden when collapsed */}
        {!effectivelyCollapsed && (
          <div className="px-3 pb-2 pt-1">
            <LanguageSwitcher variant="sidebar" />
          </div>
        )}

        <BackgroundSyncIndicator />

        {/* User block */}
        <div className={`${effectivelyCollapsed ? "px-3 pb-3 pt-1" : "p-4 pt-2"}`}>
          {effectivelyCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link href="/app/settings">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg mx-auto cursor-pointer hover:bg-sidebar-accent/50 transition-colors relative">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    {oldThreadPending > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">
                        {oldThreadPending > 99 ? "99+" : oldThreadPending}
                      </span>
                    )}
                  </div>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {t("app_nav.settings")}
              </TooltipContent>
            </Tooltip>
          ) : (
            <DesktopUserBlock
              name={user?.name}
              email={user?.email}
              oldThreadPending={oldThreadPending}
            />
          )}
        </div>

        {/* Collapse toggle button */}
        <button
          onClick={toggleSidebar}
          aria-label={effectivelyCollapsed ? "Développer la barre latérale" : "Réduire la barre latérale"}
          className={`w-full flex items-center py-2.5 text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-colors border-t border-sidebar-border ${
            effectivelyCollapsed ? "justify-center" : "justify-end px-4"
          }`}
        >
          {effectivelyCollapsed ? (
            <PanelLeftOpen className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </button>
      </aside>
      </TooltipProvider>

      {/* ── Main column ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Mobile header */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-background sticky top-0 z-30">
          {/* Left: logo */}
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer select-none">
              <Logo size={28} />
              <span className="font-bold text-lg tracking-tight text-foreground">
                OneNext
              </span>
            </div>
          </Link>

          {/* Right: profile avatar → opens nav sheet */}
          <Sheet open={profileOpen} onOpenChange={setProfileOpen}>
            <SheetTrigger asChild>
              <button
                aria-label={
                  mailUnread > 0
                    ? t("shell.unread_aria", { count: mailUnread })
                    : t("shell.profile_aria")
                }
                data-testid="mobile-profile-trigger"
                className="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {mailUnread > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background"
                  >
                    {mailUnread > 99 ? "99+" : mailUnread}
                  </span>
                )}
              </button>
            </SheetTrigger>

            <SheetContent
              side="right"
              className="w-72 p-0 bg-sidebar border-l border-sidebar-border flex flex-col"
            >
              {/* User info block */}
              <SheetHeader className="p-6 pb-4">
                <SheetTitle className="sr-only">{t("shell.nav_sheet_title")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("shell.nav_sheet_desc")}
                </SheetDescription>
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-base font-bold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-semibold text-sidebar-foreground truncate">
                      {user?.name || t("app_nav.user_fallback")}
                    </p>
                    <p className="text-xs text-sidebar-foreground/60 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </SheetHeader>

              <Separator className="bg-sidebar-border" />

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto">
                {/* Nav links */}
                <div className="px-3 py-4">
                  <NavLinks
                    location={location}
                    onNavigate={() => setProfileOpen(false)}
                    variant="sheet"
                    mailUnread={mailUnread}
                    pendingActions={pendingActions}
                    delegatedOverdue={delegatedOverdue}
                    urgentTasks={urgentTasks}
                    projectSuggestionsPending={projectSuggestionsPending}
                    showAdmin={isAdmin && !impersonation}
                    mailSyncStatus={mailSyncStatus}
                  />
                </div>

                <Separator className="bg-sidebar-border" />

                {/* Visite guidée + Demo mode toggle */}
                <div className="px-3 pt-2 space-y-1">
                  <button
                    onClick={() => { handleStartTour(); setProfileOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
                  >
                    <BookOpen className="w-5 h-5 shrink-0" />
                    <span className="flex-1 text-left">Visite guidée</span>
                  </button>
                  {isDemoActive ? (
                    <button
                      onClick={() => { handleDisableDemo(); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 transition-colors"
                    >
                      <Layers className="w-5 h-5 shrink-0" />
                      <span className="flex-1 text-left">Mode démo actif</span>
                      <X className="w-4 h-4 opacity-60" />
                    </button>
                  ) : (
                    <button
                      onClick={() => { void handleEnableDemo(); setProfileOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
                    >
                      <Layers className="w-5 h-5 shrink-0" />
                      <span className="flex-1 text-left">Essayer la démo</span>
                    </button>
                  )}
                </div>

                {/* Language switcher */}
                <div className="px-3 pt-3">
                  <LanguageSwitcher variant="sidebar" />
                </div>

                {/* Settings + Logout */}
                <div className="p-3 space-y-1">
                  <Link
                    href="/app/settings"
                    onClick={() => setProfileOpen(false)}
                  >
                    <span className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors cursor-pointer">
                      <SettingsIcon className="w-5 h-5 shrink-0" />
                      <span className="flex-1">{t("app_nav.settings")}</span>
                      {oldThreadPending > 0 && (
                        <span
                          aria-label={t("shell.old_threads_aria", { count: oldThreadPending })}
                          className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center"
                          data-testid="badge-old-threads-mobile"
                        >
                          {oldThreadPending > 99 ? "99+" : oldThreadPending}
                        </span>
                      )}
                    </span>
                  </Link>
                  <button
                    onClick={() => { setProfileOpen(false); void signOut({ redirectUrl: "/" }); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive transition-colors"
                    data-testid="btn-signout-mobile"
                  >
                    <LogOut className="w-5 h-5 shrink-0" />
                    <span className="flex-1 text-left">Déconnexion</span>
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        {impersonation && (
          <div
            className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-amber-500/40 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 px-4 py-2 text-sm shadow-sm"
            data-testid="impersonation-banner"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-4 h-4 shrink-0" />
              <span className="truncate">
                {t("shell.impersonation_banner")}{" "}
                <strong className="font-semibold">{impersonation.email}</strong>
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleStopImpersonation}
              className="bg-white/80 hover:bg-white text-amber-900 border-amber-400"
              data-testid="impersonation-stop"
            >
              {t("shell.impersonation_stop")}
            </Button>
          </div>
        )}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <DemoModeBanner />
          <MailConnectBanner />
          <AiBudgetBanner />
          <div className={`flex-1 min-h-0 overflow-x-hidden ${isMailReading ? "overflow-hidden" : "overflow-y-auto"}`}>
            {children}
          </div>
        </main>
        <DemoTutorial />
        <AppLegalFooter />
        <CaptureFab onOpen={() => setCaptureOpen(true)} />
        <FirstSyncRecapDialog />
        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          onOpenCapture={() => setCaptureOpen(true)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
        />
        <KeyboardShortcutsDialog
          open={shortcutsOpen}
          onOpenChange={setShortcutsOpen}
        />
        <CaptureDialog open={captureOpen} onOpenChange={setCaptureOpen} />
      </div>
    </div>
  );
}

function AppLegalFooter() {
  const { t } = useTranslation();
  const links = [
    { href: "/legal/mentions", label: t("footer.legal_mentions_short") },
    { href: "/legal/cgu", label: t("footer.cgu") },
    { href: "/legal/cgv", label: t("footer.cgv") },
    { href: "/legal/confidentialite", label: t("footer.confidentiality") },
    { href: "/legal/cookies", label: t("footer.cookies") },
    { href: "/legal/dpa", label: t("footer.dpa") },
    { href: "/contact", label: t("footer.contact") },
  ];
  return (
    <footer
      className="border-t border-border/60 px-4 py-3 text-[11px] text-muted-foreground bg-background/50"
      data-testid="app-legal-footer"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {links.map((l) => (
          <Link
            key={l.href}
            href={l.href}
            className="hover:text-foreground transition-colors"
          >
            {l.label}
          </Link>
        ))}
      </div>
    </footer>
  );
}
